<template>
  <div class="company-service-panel">
    <div class="panel">
      <div class="panel-title">
        {{
          mode === 'recent'
            ? $t('hit-admin.subscriptions.companyService.recentTitle')
            : $t('hit-admin.subscriptions.companyService.byCompanyTitle')
        }}
      </div>
      <div class="panel-subtitle">
        {{ $t('hit-admin.subscriptions.companyService.subtitle') }}
      </div>

      <div class="panel-grid">
        <hit-select
          v-if="mode === 'by_company'"
          class="min-w-[32rem] w-full"
          :label="$t('hit-admin.subscriptions.companyService.company')"
          :options="companyOptions"
          :value="selectedCompanyId"
          item-id-property="id"
          :label-fields="['label']"
          filterable
          clearable
          @change="onCompanyChange"
        />

        <hit-input
          v-if="mode === 'recent'"
          class="min-w-[10rem]"
          type="number"
          :label="$t('hit-admin.subscriptions.companyService.limit')"
          :value="recentLimit"
          @change="onRecentLimitChange"
        />

        <hit-input
          class="min-w-[12rem]"
          type="date"
          :label="$t('hit-admin.subscriptions.companyService.dateFrom')"
          :value="dateFrom"
          clearable
          @change="onDateFromChange"
        />

        <hit-input
          class="min-w-[12rem]"
          type="date"
          :label="$t('hit-admin.subscriptions.companyService.dateTo')"
          :value="dateTo"
          clearable
          @change="onDateToChange"
        />
      </div>

      <div class="toolbar">
        <hit-button
          :label="$t('hit-admin.subscriptions.companyService.refresh')"
          color="button"
          prefix-icon="refresh"
          :disabled="loading || !canLoad"
          @click="loadRows"
        />
        <hit-button
          :label="$t('hit-admin.subscriptions.companyService.add')"
          color="accent"
          prefix-icon="add"
          :disabled="loading || !canAdd"
          @click="openCreate"
        />
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>

      <div v-if="loading" class="loading-state">
        {{ $t('hit-admin.subscriptions.companyService.loading') }}
      </div>

      <div v-else-if="!canLoad" class="empty-state">
        {{ $t('hit-admin.subscriptions.companyService.selectCompanyHint') }}
      </div>

      <div v-else-if="rows.length === 0" class="empty-state">
        {{ $t('hit-admin.subscriptions.companyService.noRows') }}
      </div>

      <div v-else class="table-wrap">
        <table class="service-table">
          <thead>
            <tr>
              <th>
                {{ $t('hit-admin.subscriptions.companyService.columns.date') }}
              </th>
              <th v-if="mode === 'recent'">
                {{
                  $t('hit-admin.subscriptions.companyService.columns.company')
                }}
              </th>
              <th>
                {{
                  $t(
                    'hit-admin.subscriptions.companyService.columns.description'
                  )
                }}
              </th>
              <th>
                {{ $t('hit-admin.subscriptions.companyService.columns.user') }}
              </th>
              <th class="col-num">
                {{
                  $t('hit-admin.subscriptions.companyService.columns.duration')
                }}
              </th>
              <th class="col-bool">
                {{
                  $t('hit-admin.subscriptions.companyService.columns.billable')
                }}
              </th>
              <th class="col-actions">
                {{
                  $t('hit-admin.subscriptions.companyService.columns.actions')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td>{{ formatDate(row.date) }}</td>
              <td v-if="mode === 'recent'">
                {{ row.company_designation || row.company_id }}
              </td>
              <td class="col-description">{{ row.description || '—' }}</td>
              <td>{{ formatUserName(row) }}</td>
              <td class="col-num">
                {{ formatDuration(row.duration_minutes) }}
              </td>
              <td class="col-bool">
                <span
                  class="pill"
                  :class="row.billable ? 'pill-ok' : 'pill-muted'"
                >
                  {{ row.billable ? 'Oui' : 'Non' }}
                </span>
              </td>
              <td class="col-actions">
                <div class="row-actions">
                  <hit-button
                    prefix-icon="edit"
                    color="panel"
                    :tooltip="$t('hit-admin.subscriptions.companyService.edit')"
                    :hide-border="true"
                    :remove-outline="true"
                    :padding-value="1"
                    @click="openEdit(row)"
                  />
                  <hit-button
                    prefix-icon="trash"
                    color="panel"
                    :tooltip="
                      $t('hit-admin.subscriptions.companyService.delete')
                    "
                    :hide-border="true"
                    :remove-outline="true"
                    :padding-value="1"
                    @click="openDelete(row)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <hit-dialog
      :visible="formDialogVisible"
      :title="formDialogTitle"
      size="large"
      :show-ok-button="false"
      :show-cancel-button="false"
      @update:visible="onFormDialogVisibleChange"
    >
      <template #body>
        <div class="form-grid">
          <hit-select
            v-if="!editingRow"
            class="w-full"
            :label="$t('hit-admin.subscriptions.companyService.company')"
            :options="companyOptions"
            :value="form.company_id"
            item-id-property="id"
            :label-fields="['label']"
            filterable
            clearable="false"
            :disabled="mode === 'by_company' && !!selectedCompanyId"
            @change="onFormCompanyChange"
          />

          <hit-input
            class="w-full"
            type="date"
            :label="$t('hit-admin.subscriptions.companyService.columns.date')"
            :value="form.date"
            @change="onFormDateChange"
          />

          <hit-input
            class="w-full"
            type="text"
            :label="
              $t('hit-admin.subscriptions.companyService.columns.description')
            "
            :value="form.description"
            @change="onFormDescriptionChange"
          />

          <hit-input
            class="w-full"
            type="number"
            :label="
              $t('hit-admin.subscriptions.companyService.durationMinutes')
            "
            :value="form.duration_minutes"
            @change="onFormDurationChange"
          />

          <hit-input-checkbox
            class="w-full"
            :values="
              $t('hit-admin.subscriptions.companyService.columns.billable')
            "
            :value="form.billable"
            @change="onFormBillableChange"
          />

          <hit-input
            class="w-full"
            type="text"
            :label="$t('hit-admin.subscriptions.companyService.userId')"
            :value="form.user_id"
            clearable
            @change="onFormUserIdChange"
          />
        </div>
        <div v-if="formError" class="alert alert-error mt-3">
          {{ formError }}
        </div>
      </template>
      <template #footer>
        <hit-button
          :label="$t('hit-admin.subscriptions.companyService.cancel')"
          color="button"
          :disabled="saving"
          @click="closeFormDialog"
        />
        <hit-button
          :label="$t('hit-admin.subscriptions.companyService.save')"
          color="accent"
          prefix-icon="save"
          :disabled="saving"
          @click="saveForm"
        />
      </template>
    </hit-dialog>

    <hit-dialog
      :visible="deleteDialogVisible"
      :title="$t('hit-admin.subscriptions.companyService.deleteTitle')"
      color="danger"
      :message="deleteDialogMessage"
      :ok-button-label="
        $t('hit-admin.subscriptions.companyService.deleteConfirm')
      "
      ok-button-color="danger"
      :show-cancel-button="true"
      :cancel-button-label="$t('hit-admin.subscriptions.companyService.cancel')"
      :ok-button-disabled="deleting"
      @update:visible="onDeleteDialogVisibleChange"
      @ok="confirmDelete"
    />
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {
  HitButton,
  HitDialog,
  HitInput,
  HitInputCheckbox,
  HitSelect,
} from '@hit/components';
import {adminApi, COMPANY_OPTIONS_MAX_LIMIT} from '@admin/services/adminApi';

const DEFAULT_RECENT_LIMIT = 100;

function emptyForm() {
  return {
    company_id: null,
    date: new Date().toISOString().slice(0, 10),
    description: '',
    duration_minutes: 60,
    billable: true,
    user_id: '',
  };
}

export default defineComponent({
  name: 'CompanyServicePanel',
  components: {
    HitButton,
    HitDialog,
    HitInput,
    HitInputCheckbox,
    HitSelect,
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ['recent', 'by_company'].includes(v),
    },
  },
  data() {
    return {
      companyOptions: [],
      selectedCompanyId: null,
      recentLimit: DEFAULT_RECENT_LIMIT,
      dateFrom: '',
      dateTo: '',
      rows: [],
      loading: false,
      errorMessage: '',
      formDialogVisible: false,
      editingRow: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      deleteDialogVisible: false,
      rowToDelete: null,
      deleting: false,
    };
  },
  computed: {
    canLoad() {
      return this.mode === 'recent' || !!this.selectedCompanyId;
    },
    canAdd() {
      return this.mode === 'recent' || !!this.selectedCompanyId;
    },
    formDialogTitle() {
      return this.editingRow
        ? this.$t('hit-admin.subscriptions.companyService.editTitle')
        : this.$t('hit-admin.subscriptions.companyService.addTitle');
    },
    deleteDialogMessage() {
      if (!this.rowToDelete) return '';
      const label = this.rowToDelete.description || this.rowToDelete.id;
      return this.$t('hit-admin.subscriptions.companyService.deleteMessage', {
        label,
      });
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
    if (this.mode === 'recent') {
      await this.loadRows();
    }
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({
          limit: COMPANY_OPTIONS_MAX_LIMIT,
        });
        this.companyOptions = (res.rows || []).map((c) => ({
          id: c.id,
          label: [c.designation, c.vat_no].filter(Boolean).join(' — ') || c.id,
        }));
      } catch (e) {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedCompanyId = value || null;
      this.rows = [];
      this.errorMessage = '';
      if (this.selectedCompanyId) {
        void this.loadRows();
      }
    },
    onRecentLimitChange(value) {
      const n = Number(value);
      this.recentLimit =
        Number.isFinite(n) && n > 0 ? Math.trunc(n) : DEFAULT_RECENT_LIMIT;
    },
    onDateFromChange(value) {
      this.dateFrom = value || '';
    },
    onDateToChange(value) {
      this.dateTo = value || '';
    },
    buildListParams() {
      const params = {};
      if (this.mode === 'by_company' && this.selectedCompanyId) {
        params.company_id = this.selectedCompanyId;
      }
      if (this.mode === 'recent') {
        params.limit = this.recentLimit;
      }
      if (this.dateFrom) params.date_from = this.dateFrom;
      if (this.dateTo) params.date_to = this.dateTo;
      return params;
    },
    async loadRows() {
      if (!this.canLoad) return;
      this.loading = true;
      this.errorMessage = '';
      try {
        this.rows = await adminApi.listCompanyServices(this.buildListParams());
      } catch (e) {
        this.rows = [];
        this.errorMessage = e?.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return '—';
      return String(value).slice(0, 10);
    },
    formatDuration(minutes) {
      const m = Number(minutes) || 0;
      const hours = (m / 60).toFixed(2);
      return `${m} min (${hours} h)`;
    },
    formatUserName(row) {
      const parts = [row.user_last_name, row.user_first_name].filter(Boolean);
      if (parts.length) {
        return parts.join(' - ');
      }
      return row.user_id || '—';
    },
    openCreate() {
      this.editingRow = null;
      this.form = emptyForm();
      if (this.mode === 'by_company' && this.selectedCompanyId) {
        this.form.company_id = this.selectedCompanyId;
      }
      this.formError = '';
      this.formDialogVisible = true;
    },
    openEdit(row) {
      this.editingRow = row;
      this.form = {
        company_id: row.company_id,
        date: this.formatDate(row.date),
        description: row.description || '',
        duration_minutes: row.duration_minutes,
        billable: row.billable !== false,
        user_id: row.user_id || '',
      };
      this.formError = '';
      this.formDialogVisible = true;
    },
    closeFormDialog() {
      this.formDialogVisible = false;
      this.editingRow = null;
      this.formError = '';
    },
    onFormDialogVisibleChange(visible) {
      if (!visible) {
        this.closeFormDialog();
      }
    },
    onFormCompanyChange(value) {
      this.form.company_id = value;
    },
    onFormDateChange(value) {
      this.form.date = value;
    },
    onFormDescriptionChange(value) {
      this.form.description = value;
    },
    onFormDurationChange(value) {
      this.form.duration_minutes = Number(value);
    },
    onFormBillableChange(value) {
      this.form.billable = !!value;
    },
    onFormUserIdChange(value) {
      this.form.user_id = value || '';
    },
    validateForm() {
      if (!this.editingRow && !this.form.company_id) {
        return this.$t(
          'hit-admin.subscriptions.companyService.errors.companyRequired'
        );
      }
      if (!this.form.date) {
        return this.$t(
          'hit-admin.subscriptions.companyService.errors.dateRequired'
        );
      }
      const duration = Number(this.form.duration_minutes);
      if (!Number.isFinite(duration) || duration <= 0) {
        return this.$t(
          'hit-admin.subscriptions.companyService.errors.durationRequired'
        );
      }
      return '';
    },
    async saveForm() {
      this.formError = this.validateForm();
      if (this.formError) return;

      this.saving = true;
      try {
        if (this.editingRow) {
          await adminApi.updateCompanyService(this.editingRow.id, {
            date: this.form.date,
            description: this.form.description || null,
            duration_minutes: Math.trunc(Number(this.form.duration_minutes)),
            billable: this.form.billable,
            user_id: this.form.user_id || null,
          });
        } else {
          await adminApi.createCompanyService({
            date: this.form.date,
            company_id: this.form.company_id,
            description: this.form.description || null,
            duration_minutes: Math.trunc(Number(this.form.duration_minutes)),
            billable: this.form.billable,
            user_id: this.form.user_id || null,
            custom_data: {},
          });
        }
        this.closeFormDialog();
        await this.loadRows();
      } catch (e) {
        this.formError = e?.message || String(e);
      } finally {
        this.saving = false;
      }
    },
    openDelete(row) {
      this.rowToDelete = row;
      this.deleteDialogVisible = true;
    },
    onDeleteDialogVisibleChange(visible) {
      if (!visible) {
        this.deleteDialogVisible = false;
        this.rowToDelete = null;
      }
    },
    async confirmDelete() {
      if (!this.rowToDelete) return;
      this.deleting = true;
      try {
        await adminApi.deleteCompanyService(this.rowToDelete.id);
        this.deleteDialogVisible = false;
        this.rowToDelete = null;
        await this.loadRows();
      } catch (e) {
        this.errorMessage = e?.message || String(e);
        this.deleteDialogVisible = false;
      } finally {
        this.deleting = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.company-service-panel {
  height: 100%;
}

.panel {
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(6px);
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.04),
    0 12px 30px rgba(16, 24, 40, 0.08);
  padding: 1.1rem 1.15rem 1.25rem;
}

.panel-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: rgba(15, 23, 42, 0.95);
}

.panel-subtitle {
  margin-top: 0.25rem;
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.35rem;
}

.panel-grid {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.toolbar {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.loading-state,
.empty-state {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  background: rgba(248, 250, 252, 1);
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
}

.alert {
  margin-top: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.65rem;
  font-size: 0.85rem;
}

.alert-error {
  background: rgba(254, 242, 242, 1);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #b91c1c;
}

.table-wrap {
  margin-top: 1rem;
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.75rem;
}

.service-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: rgba(248, 250, 252, 1);
    font-weight: 600;
    color: rgba(51, 65, 85, 1);
    white-space: nowrap;
  }

  tbody tr:hover {
    background: rgba(248, 250, 252, 0.65);
  }
}

.col-description {
  min-width: 14rem;
  max-width: 28rem;
  word-break: break-word;
}

.col-num {
  white-space: nowrap;
  text-align: right;
}

.col-bool {
  white-space: nowrap;
  text-align: center;
}

.col-actions {
  white-space: nowrap;
  width: 5rem;
}

.row-actions {
  display: flex;
  gap: 0.15rem;
  justify-content: center;
}

.pill {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pill-ok {
  background: rgba(220, 252, 231, 1);
  color: #166534;
}

.pill-warn {
  background: rgba(254, 243, 199, 1);
  color: #92400e;
}

.pill-muted {
  background: rgba(241, 245, 249, 1);
  color: rgba(100, 116, 139, 1);
}

.form-grid {
  display: grid;
  gap: 0.75rem;
}
</style>
